import { maxDotNumber } from "@zyzgroup/core-common";
import { Matrix, Matrix4 } from "../Matrix";
import { angle, distance, toRadian } from "../math";
import { YZCanvasContext } from "../../canvas/YZCanvasContext";
import DOMSVGUtil, { type TSVGParsedPathData } from "../../svg/DOMSVGUtil";
import { a2c, l2c, q2c } from "../../svg/line2c";
import L from "./L";
import CubicBezier from "./CubicBezier";
import QuadraticBezier from "./QuadraticBezier";
import Arc from "./Arc";

/// 连续的曲线
export class PathD {
  /// cX cY 为相对于自身 (0,0) 点的相对值
  public cX: number;
  public cY: number;
  public x: number;
  public y: number;
  public degree: number;
  public scalarX: number;
  public scalarY: number;

  /// 绝对值
  public startPathX = 0;
  public startPathY = 0;
  public prevPathX = 0;
  public prevPathY = 0;
  public normalizedLines: CubicBezier[] = [];

  // d = "M0,0"
  // d = [['C',1,2,3,4,5,6]]
  constructor(options?: {
    d?: string | TSVGParsedPathData[] | PathD;
    originMatrix?: Matrix | Matrix4 | DOMMatrix;
    centerX?: number;
    centerY?: number;
    x?: number;
    y?: number;
    degree?: number;
    scalar?: number;
    scalarX?: number;
    scalarY?: number;
  }) {
    this.cX = 0;
    this.cY = 0;
    this.x = options?.x || 0;
    this.y = options?.y || 0;
    if (
      typeof options?.centerX == "number" &&
      typeof options?.centerY == "number"
    ) {
      this.cX = options.centerX - this.x;
      this.cY = options.centerY - this.y;
    }
    this.degree = options?.degree || 0;
    this.scalarX = 1;
    this.scalarY = 1;
    if (options?.scalar !== void 0) {
      this.scalarX = options.scalar;
      this.scalarY = options.scalar;
    }
    if (options?.scalarX !== void 0) {
      this.scalarX = options.scalarX;
    }
    if (options?.scalarY !== void 0) {
      this.scalarY = options.scalarY;
    }
    this.reset(options?.d, options?.originMatrix);
  }

  public reset(
    d?: string | TSVGParsedPathData[] | PathD,
    originMatrix?: Matrix | Matrix4 | DOMMatrix
  ) {
    if (typeof d == "string") {
      this.resetFromSVGPathData(DOMSVGUtil.parseSVGPath(d), originMatrix);
    } else if (d instanceof PathD) {
      this.resetFromSVGPathData(d.toSVGPathData(), originMatrix);
    } else {
      this.resetFromSVGPathData(d || [], originMatrix);
    }
  }

  private resetFromSVGPathData(
    data: TSVGParsedPathData[],
    originMatrix?: Matrix | Matrix4 | DOMMatrix
  ) {
    this.normalizedLines = [];
    /// paths 只包含 M 或 C 语句
    const paths = DOMSVGUtil.normalizeSVGPath(
      DOMSVGUtil.absolutizeSVGPath(data)
    );
    this.prevPathX = this.startPathX = 0;
    this.prevPathY = this.startPathY = 0;
    let isResetStart = false;
    for (let i = 0, count = paths.length; i < count; i++) {
      const path = paths[i];
      const command = path[0];
      if (command == "M") {
        if (!isResetStart) {
          isResetStart = true;
          this.startPathX = path[1];
          this.startPathY = path[2];
        }
        this.prevPathX = path[1];
        this.prevPathY = path[2];
        continue;
      }
      this.appendLine(
        new CubicBezier({
          c1x: path[1],
          c1y: path[2],
          c2x: path[3],
          c2y: path[4],
          toX: path[5],
          toY: path[6]
        })
      );
    }
    if (originMatrix) {
      ({ x: this.startPathX, y: this.startPathY } = originMatrix.transformPoint(
        {
          x: this.startPathX,
          y: this.startPathY
        }
      ));
      ({ x: this.prevPathX, y: this.prevPathY } = originMatrix.transformPoint({
        x: this.prevPathX,
        y: this.prevPathY
      }));
      this.normalizedLines.forEach((l) => {
        ({ x: l.x, y: l.y } = originMatrix.transformPoint({ x: l.x, y: l.y }));
        ({ x: l.c1x, y: l.c1y } = originMatrix.transformPoint({
          x: l.c1x,
          y: l.c1y
        }));
        ({ x: l.c2x, y: l.c2y } = originMatrix.transformPoint({
          x: l.c2x,
          y: l.c2y
        }));
        ({ x: l.toX, y: l.toY } = originMatrix.transformPoint({
          x: l.toX,
          y: l.toY
        }));
      });
    }
  }

  static fromPolymorphPoints(points: number[]): PathD {
    const d = new PathD();
    d.prevPathX = d.startPathX = points[0] || 0;
    d.prevPathY = d.startPathY = points[1] || 0;
    for (let k = 2, count = points.length; k + 5 < count; k += 6) {
      d.appendLine(
        new CubicBezier({
          c1x: points[k],
          c1y: points[k + 1],
          c2x: points[k + 2],
          c2y: points[k + 3],
          toX: points[k + 4],
          toY: points[k + 5]
        })
      );
    }
    return d;
  }

  static fromLines(lines: L[]): PathD {
    const d = new PathD();
    d.prevPathX = d.startPathX = lines[0].x || 0;
    d.prevPathY = d.startPathY = lines[0].y || 0;
    for (let i = 0, count = lines.length; i < count; i++) {
      d.appendLine(lines[i]);
    }
    return d;
  }

  /// 涉及 this.prevPathX + this.prevPathY
  private appendLine(line: L) {
    if (line instanceof Arc) {
      const curves = a2c(
        this.prevPathX,
        this.prevPathY,
        line.toX,
        line.toY,
        line.largeArcFlag,
        line.sweepFlag,
        line.rx,
        line.ry,
        line.xAxisRotate
      );
      curves.forEach((curve) => {
        const [x0, y0, x1, y1, x2, y2, x, y] = curve;
        this.normalizedLines.push(
          new CubicBezier({
            x: this.prevPathX,
            y: this.prevPathY,
            c1x: x1,
            c1y: y1,
            c2x: x2,
            c2y: y2,
            toX: x,
            toY: y
          })
        );
        this.prevPathX = x;
        this.prevPathY = y;
      });
      return;
    }
    let x1, y1, x2, y2, toX, toY;
    if (line instanceof CubicBezier) {
      x1 = line.c1x;
      y1 = line.c1y;
      x2 = line.c2x;
      y2 = line.c2y;
      toX = line.toX;
      toY = line.toY;
    } else if (line instanceof QuadraticBezier) {
      [x1, y1, x2, y2, toX, toY] = q2c(
        this.prevPathX,
        this.prevPathY,
        line.cx,
        line.cy,
        line.toX,
        line.toY
      );
    } else {
      /// L
      [x1, y1, x2, y2, toX, toY] = l2c(
        this.prevPathX,
        this.prevPathY,
        line.toX,
        line.toY
      );
    }
    this.normalizedLines.push(
      new CubicBezier({
        x: this.prevPathX,
        y: this.prevPathY,
        c1x: x1,
        c1y: y1,
        c2x: x2,
        c2y: y2,
        toX: toX,
        toY: toY
      })
    );
    this.prevPathX = toX;
    this.prevPathY = toY;
  }

  /// 绝对值
  toSVGPathData(): TSVGParsedPathData[] {
    const { startPathX, startPathY, normalizedLines } = this.updateTransform();
    const data: TSVGParsedPathData[] = [];
    data.push(["M", startPathX, startPathY]);
    for (let i = 0, count = normalizedLines.length; i < count; i++) {
      data.push(...normalizedLines[i].toSVGPathData());
    }
    // data.push(["z"]);
    return data;
  }

  toSVGPathD(): string {
    return DOMSVGUtil.SVGPathDataToD(this.toSVGPathData());
  }

  /// PathD 自己管理 canvas transform
  toCanvasContext(ctx: YZCanvasContext) {
    const { startPathX, startPathY, normalizedLines } = this.updateTransform();
    ctx.beginPath();
    ctx.moveTo(startPathX, startPathY);
    normalizedLines.forEach((c) => {
      c.toCanvasContext(ctx);
    });
    // ctx.closePath();
  }

  /// 绝对值
  public getBoundingClientRect() {
    const { normalizedLines } = this.updateTransform();
    const X: number[] = [];
    const Y: number[] = [];
    normalizedLines.forEach((l) => {
      const rect = l.getBoundingClientRect();
      X.push(rect.x, rect.x + rect.width);
      Y.push(rect.y, rect.y + rect.height);
    });
    if (X.length == 0 || Y.length == 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    const xmin = Math.min.apply(0, X);
    const ymin = Math.min.apply(0, Y);
    const xmax = Math.max.apply(0, X);
    const ymax = Math.max.apply(0, Y);
    return {
      x: xmin,
      y: ymin,
      width: xmax - xmin,
      height: ymax - ymin
    };
  }

  public update(t: {
    cX?: number;
    cY?: number;
    x: number;
    y: number;
    degree: number;
    scalarX: number;
    scalarY: number;
  }) {
    this.cX = typeof t.cX == "number" ? t.cX : this.cX;
    this.cY = typeof t.cY == "number" ? t.cY : this.cY;
    this.x = t.x;
    this.y = t.y;
    this.degree = t.degree;
    this.scalarX = t.scalarX;
    this.scalarY = t.scalarY;
  }

  private updateTransform() {
    /// 移动的是坐标系
    /// the order of these are important
    const m = new Matrix();

    /// followMouse 不对
    // m.translate(
    //   this.x - this.prevX + this.prevCX,
    //   this.y - this.prevY + this.prevCY
    // );
    // m.scale(1 / this.prevScalarX, 1 / this.prevScalarY);
    // m.rotate(-this.prevDegree);
    // m.translate(this.cX - this.prevCX, this.cY - this.prevCY);
    // m.rotate(this.degree);
    // m.scale(this.scalarX, this.scalarY);
    // m.translate(-this.cX, -this.cY);

    /// 连续精度影响 不对
    // m.scale(
    //   this.scalarX / this.prevScalarX,
    //   this.scalarY / this.prevScalarY,
    //   this.cX + this.x,
    //   this.cY + this.y
    // );
    // m.rotate(this.degree - this.prevDegree, this.cX + this.x, this.cY + this.y);
    // m.translate(this.x - this.prevX, this.y - this.prevY);

    /// 连续精度影响 不对
    // m.translate(this.x + this.cX, this.y + this.cY);
    // m.scale(this.scalarX / this.prevScalarX, this.scalarY / this.prevScalarY);
    // m.rotate(this.degree - this.prevDegree);
    // m.translate(-this.prevX - this.cX, -this.prevY - this.cY);

    m.translate(this.x + this.cX, this.y + this.cY);
    m.rotate(this.degree);
    m.scale(this.scalarX, this.scalarY);
    m.translate(-this.cX, -this.cY);

    if (!m.isIdentity) {
      let startPathX = this.startPathX;
      let startPathY = this.startPathY;
      ({ x: startPathX, y: startPathY } = m.transformPoint({
        x: startPathX,
        y: startPathY
      }));
      const normalizedLines: CubicBezier[] = [];
      this.normalizedLines.forEach((l) => {
        let x = l.x;
        let y = l.y;
        let c1x = l.c1x;
        let c1y = l.c1y;
        let c2x = l.c2x;
        let c2y = l.c2y;
        let toX = l.toX;
        let toY = l.toY;
        ({ x: x, y: y } = m.transformPoint({ x: x, y: y }));
        ({ x: c1x, y: c1y } = m.transformPoint({ x: c1x, y: c1y }));
        ({ x: c2x, y: c2y } = m.transformPoint({ x: c2x, y: c2y }));
        ({ x: toX, y: toY } = m.transformPoint({ x: toX, y: toY }));
        normalizedLines.push(
          new CubicBezier({ x, y, c1x, c1y, c2x, c2y, toX, toY })
        );
      });
      return {
        startPathX,
        startPathY,
        normalizedLines
      };
    } else {
      return {
        startPathX: this.startPathX,
        startPathY: this.startPathY,
        normalizedLines: this.normalizedLines
      };
    }
  }

  /// returns length in pixels
  getTotalLength(): number {
    const { normalizedLines } = this.updateTransform();
    /// 实际值：1718.289461119151
    return normalizedLines.reduce((acc, l) => (acc += l.length()), 0);
    /// 实际值：1718.2906494140625
    // const el = DOMSVGUtil.createSvgPath(this.toSVGPathD());
    // return el.getTotalLength();
  }

  getPointAtLength(length: number): {
    x: number;
    y: number;
    alpha: number; // degree: angle of derivative
  } {
    const { normalizedLines } = this.updateTransform();
    /// 实际值：{x: 69.12001660070015, y: 139.8368628028231}
    let len = 0;
    const linesCount = normalizedLines.length;
    for (let i = 0; i < linesCount; i++) {
      const p = normalizedLines[i];
      const l = p.length();
      if (len + l >= length) {
        return p.getPointAtSegmentLength(length - len);
      }
      len += l;
    }
    return normalizedLines[linesCount - 1].getPointAtSegmentLength(1);
    /// 实际值：{x: 69.12151336669922, y: 139.8350830078125}
    // const el = DOMSVGUtil.createSvgPath(this.toSVGPathD());
    // return el.getPointAtLength(length);
  }

  /// 与 getPointAtLength 类似
  /// t (0.0 to 1.0) : The fraction of the overall duration of the path
  interpolate(t: number): { x: number; y: number } {
    const { startPathX, startPathY, normalizedLines } = this.updateTransform();
    const linesCount = normalizedLines.length;
    if (linesCount === 0) {
      return { x: startPathX, y: startPathY };
    } else if (linesCount === 1) {
      return normalizedLines[0].interpolate(t);
    } else {
      let ratio = t * linesCount;
      let path = normalizedLines[0];
      ratio--;
      if (ratio > 1) {
        for (let i = 1; i < linesCount; i++) {
          path = normalizedLines[i];
          if (ratio > 1) {
            ratio -= 1;
          } else {
            break;
          }
        }
      }
      return path.interpolate(ratio);
    }
  }

  /// from | to  : 0 ~ 1
  getSubpath(from: number, to: number): PathD {
    if (this.getTotalLength() - to < 1e-6) {
      return this.getSubpathsAtLength(this, from);
    } else {
      const a = this.getSubpathsAtLength(this, to, 1);
      return from ? this.getSubpathsAtLength(a, from) : a;
    }
  }

  getSubpathsAtLength(path: PathD, length: number, onlystart?: number): PathD {
    const { startPathX, startPathY, normalizedLines } = path.updateTransform();
    /// 从 0 到 length 的部分
    let start: PathD | null = null;
    let len = 0;
    // const sp = new PathD({ x: path.x, y: path.y });
    const sp = new PathD();
    sp.prevPathX = sp.startPathX = startPathX;
    sp.prevPathY = sp.startPathY = startPathY;
    for (let i = 0, ii = normalizedLines.length; i < ii; i++) {
      const p = normalizedLines[i];
      const l = p.length();
      if (len + l > length) {
        if (!start) {
          const point = p.getPointAtSegmentLength(length - len);
          sp.appendLine(
            new CubicBezier({
              c1x: point.start.x,
              c1y: point.start.y,
              c2x: point.m.x,
              c2y: point.m.y,
              toX: point.x,
              toY: point.y
            })
          );
          if (onlystart) {
            return sp;
          }
          start = sp;
          /// 从 length 到 end 的部分
          // sp.prevPathX = point.x;
          // sp.prevPathY = point.y;
          sp.appendLine(
            new CubicBezier({
              c1x: point.n.x,
              c1y: point.n.y,
              c2x: point.end.x,
              c2y: point.end.y,
              toX: p.x,
              toY: p.y
            })
          );
          len += l;
          continue;
        }
      }
      len += l;
      sp.appendLine(p);
    }
    // sp.updateTransform();
    return sp;
  }

  /// drag along path
  closestPoint(
    pointX: number,
    pointY: number
  ): {
    point: {
      x: number;
      y: number;
    };
    rotation: number;
    distance: number;
  } {
    const pathLength = this.getTotalLength();
    let best = { x: pointX, y: pointY };
    let bestLength = 0;
    let bestDistance = Infinity;
    let precision = 8;

    // linear scan for coarse approximation
    for (
      let scanLength = 0;
      scanLength <= pathLength;
      scanLength += precision
    ) {
      const scan = this.getPointAtLength(scanLength);
      const scanDistance = Math.pow(
        distance(scan.x, scan.y, pointX, pointY),
        2
      );
      if (scanDistance < bestDistance) {
        best = scan;
        bestLength = scanLength;
        bestDistance = scanDistance;
      }
    }

    // binary search for precise estimate
    precision /= 2;
    while (precision > 0.5) {
      const beforeLength = bestLength - precision;
      const before = this.getPointAtLength(beforeLength);
      const beforeDistance = Math.pow(
        distance(before.x, before.y, pointX, pointY),
        2
      );
      const afterLength = bestLength + precision;
      const after = this.getPointAtLength(afterLength);
      const afterDistance = Math.pow(
        distance(after.x, after.y, pointX, pointY),
        2
      );
      if (beforeLength >= 0 && beforeDistance < bestDistance) {
        best = before;
        bestLength = beforeLength;
        bestDistance = beforeDistance;
      } else if (afterLength <= pathLength && afterDistance < bestDistance) {
        best = after;
        bestLength = afterLength;
        bestDistance = afterDistance;
      } else {
        precision /= 2;
      }
    }

    const p = this.getPointAtLength(
      bestLength + (bestLength === pathLength ? -0.1 : 0.1)
    );

    return {
      point: best,
      rotation:
        bestLength >= pathLength
          ? angle(p.x, p.y, best.x, best.y)
          : angle(best.x, best.y, p.x, p.y),
      distance: Math.sqrt(bestDistance)
    };
  }

  get polymorphPoints(): number[] {
    const { startPathX, startPathY, normalizedLines } = this.updateTransform();
    return [startPathX, startPathY].concat(
      normalizedLines.reduce(
        (acc, l) => acc.concat([l.c1x, l.c1y, l.c2x, l.c2y, l.toX, l.toY]),
        [] as number[]
      )
    );
  }

  perimeterPoints(): number {
    const pts = this.polymorphPoints;
    const n = pts.length;
    let x2 = pts[n - 2];
    let y2 = pts[n - 1];
    let p = 0;
    for (let i = 0; i < n; i += 6) {
      p += distance(pts[i], pts[i + 1], x2, y2);
      x2 = pts[i];
      y2 = pts[i + 1];
    }
    return Math.floor(p);
  }

  expendFill(totalLength: number): TSVGParsedPathData[] {
    const ns = this.polymorphPoints;
    const nsLength = ns.length;
    let needAdded = totalLength - nsLength;
    const ratio = Math.ceil(totalLength / nsLength);
    const points: TSVGParsedPathData[] = [];
    points.push(["M", ns[0], ns[1]]);
    let k = 2;
    while (k + 5 <= nsLength - 1) {
      const c1x = ns[k++];
      const c1y = ns[k++];
      const c2x = ns[k++];
      const c2y = ns[k++];
      const x = ns[k++];
      const y = ns[k++];
      points.push(["C", c1x, c1y, c2x, c2y, x, y]);
      let f = 0;
      while (f < ratio && needAdded > 0) {
        points.push(["C", x, y, x, y, x, y]);
        needAdded -= 6;
        f++;
      }
    }
    return points;
  }

  static mixPoints(a: number[], b: number[], o: number): number[] {
    const alen = a.length;
    const results = new Array(alen);
    for (let i = 0; i < alen; i++) {
      results[i] = maxDotNumber(a[i] + (b[i] - a[i]) * o, 6);
    }
    return results;
  }

  /// offset : 0 ~ 1
  static getPathInterpolator(
    left: TSVGParsedPathData[],
    right: TSVGParsedPathData[]
  ): (offset: number) => TSVGParsedPathData[] {
    return (offset: number) => {
      if (Math.abs(offset - 0) < Number.EPSILON) {
        // return DOMSVGUtil.SVGPathDataToD(left);
        return left;
      }
      if (Math.abs(offset - 1) < Number.EPSILON) {
        // return DOMSVGUtil.SVGPathDataToD(right);
        return right;
      }
      const path = PathD.fromPolymorphPoints(
        PathD.mixPoints(
          new PathD({ d: left }).polymorphPoints,
          new PathD({ d: right }).polymorphPoints,
          offset
        )
      );
      return path.toSVGPathData();
    };
  }
}

export default PathD;
